import { useEffect, useMemo, useState } from "react";
import {
  GridColDef,
  DataGrid,
  GridRowParams,
  GridValueFormatterParams,
  GridSortModel,
  GridRenderCellParams,
  GridValueGetterParams,
  GridCellParams,
  GridApi,
  GridRowId,
  GridStateColDef,
  GridValidRowModel,
} from "@mui/x-data-grid";
import DataGridNoRowsOverlay from "./DatagridLayout/DataGridNoRowsOverlay";
import DataGridFooter from "./DatagridLayout/Footer/DataGridFooter";
import DataGridFooterPagination from "./DatagridLayout/Footer/DataGridFooterPagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { t } from "i18next";
import * as React from "react";

type DataRow = Record<string, any> & { id: string | number };

interface GenericDatagridProps<T extends DataRow = DataRow>
  extends React.HTMLAttributes<HTMLDivElement> {
  onRowClick?: (params: GridRowParams<T>) => void;
  data: T[];
  columns: GridColDef<T>[];
  disableHeaderCells?: string[];
}

function GenericDatagrid<T extends DataRow = DataRow>(
  props: GenericDatagridProps<T>
) {
  const { data, columns, onRowClick, disableHeaderCells, ...rest } = props;
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const pageSize = 25;
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleResize = () => {
    setWindowSize(window.innerWidth);
  };

  const highlightSearchTerm = (text: string | null | undefined, searchTerm: string) => {
    if (!searchTerm || typeof text !== "string") return text;

    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    try {
      const parts = text.toString().split(new RegExp(`(${escapedSearchTerm})`, "gi"));

      return (
        <>
          {parts.map((part, i) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
              <span key={i} className="text-accent">
                {part}
              </span>
            ) : (
              part
            )
          )}
        </>
      );
    } catch (error) {
      console.warn("Error highlighting search term:", error);
      return text;
    }
  };

  const processedColumns: GridColDef<T>[] = columns.map((column) => {
    let processedColumn = { ...column };

    // Handle type: 'date' for special sorting
    if (column.type === "date") {
      processedColumn.sortComparator = (
        _v1: unknown,
        _v2: unknown,
        param1: GridValueFormatterParams<T>,
        param2: GridValueFormatterParams<T>
      ) => {
        const date1 = new Date(param1.api.getRow(param1.id)[column.field as keyof T] as string);
        const date2 = new Date(param2.api.getRow(param2.id)[column.field as keyof T] as string);

        if (isNaN(date1.getTime()) && isNaN(date2.getTime())) return 0;
        if (isNaN(date1.getTime())) return 1;
        if (isNaN(date2.getTime())) return -1;

        return date1.getTime() - date2.getTime();
      };
    }

    if (column.renderCell) {
      return {
        ...processedColumn,
        renderCell: (params: GridRenderCellParams<unknown, T, unknown>) => {
          const originalRenderResult = column.renderCell!(params);

          if (
            !React.isValidElement(originalRenderResult) ||
            column.field === "actions" ||
            column.field === "__check__" ||
            column.filterable === false
          ) {
            return originalRenderResult;
          }

          const highlightSearchTermInner = (text: string, searchTerm: string): React.ReactNode => {
            if (!searchTerm) return text;

            const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
            return parts.map((part, i) =>
              part.toLowerCase() === searchTerm.toLowerCase() ? (
                <span key={i} className="text-accent">
                  {part}
                </span>
              ) : (
                part
              )
            );
          };

          const highlightTextInElement = (
            element: React.ReactElement,
            searchTerm: string
          ): React.ReactElement => {
            if (element.props && element.props.children) {
              if (typeof element.props.children === "string") {
                return React.cloneElement(
                  element,
                  element.props,
                  highlightSearchTermInner(element.props.children, searchTerm)
                );
              }

              if (Array.isArray(element.props.children)) {
                const newChildren = element.props.children.map((child: React.ReactNode) => {
                  if (typeof child === "string") {
                    return highlightSearchTermInner(child, searchTerm);
                  }
                  if (React.isValidElement(child)) {
                    return highlightTextInElement(child, searchTerm);
                  }
                  return child;
                });

                return React.cloneElement(element, element.props, ...newChildren);
              }
            }

            return element;
          };

          return highlightTextInElement(originalRenderResult, searchTerm);
        },
      };
    }

    return {
      ...processedColumn,
      renderCell: (params: GridRenderCellParams<unknown, T, unknown>) => {
        const value = column.valueGetter
          ? column.valueGetter(params as GridValueGetterParams<unknown, T>)
          : params.value;

        return (
          <div className="dash-on-empty overflow-hidden text-ellipsis whitespace-nowrap w-full">
            {highlightSearchTerm(value as string, searchTerm)}
          </div>
        );
      },
    };
  });

  useEffect(() => {
    if (!search.trim()) {
      setFilteredData(data);
      setSearchTerm("");
      setCurrentPage(0);
      return;
    }

    const searchTermLower = search.toLowerCase();
    setSearchTerm(searchTermLower);

    const filtered = data.filter((row) => {
      return columns.some((column) => {
        const mockApi: Partial<GridApi> = {};

        const params: GridValueGetterParams<unknown, T> = {
          id: row.id,
          field: column.field,
          row,
          value: row[column.field as keyof T],
          colDef: { ...column, computedWidth: 100 } as GridStateColDef,
          api: mockApi as GridApi,
          getValue: (_id: GridRowId, field: string) => row[field as keyof T],
          tabIndex: 0,
          rowNode: { ...({} as GridValidRowModel), id: row.id, parent: null, depth: 0 } as any,
          cellMode: "view",
          hasFocus: false,
        };

        const fieldValue = column.valueGetter
          ? column.valueGetter(params)
          : row[column.field as keyof T];

        const isFilterable =
          column.filterable !== false &&
          column.field !== "actions" &&
          column.field !== "__check__";

        if (!isFilterable || !fieldValue) return false;

        return String(fieldValue).toLowerCase().includes(searchTermLower);
      });
    });

    setFilteredData(filtered);
    setCurrentPage(0);
  }, [search, data, columns]);

  const sortedFilteredData = useMemo(() => {
    if (!sortModel.length) return filteredData;

    const sorted = [...filteredData];
    const { field, sort } = sortModel[0];

    sorted.sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sort === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sort === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return sorted;
  }, [filteredData, sortModel]);

  const handleCellClick = (params: GridCellParams) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    if (onRowClick) onRowClick({ ...params, columns } as GridRowParams<T>);
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(sortedFilteredData.length / pageSize) - 1;
    if (currentPage < maxPage) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage((prevPage) => prevPage - 1);
  };

  const UpIcon = () => <FontAwesomeIcon icon={faArrowUp} className="text-gray-500 mx-[2px] text-[16px]" />;
  const DownIcon = () => <FontAwesomeIcon icon={faArrowDown} className="text-gray-500 mx-[2px] text-[16px]" />;

  return (
    <div className={` ${rest.className}`} {...rest}>
      <div
        onClick={() => {
          const input = document.getElementById("table-search") as HTMLInputElement | null;
          if (input) input.focus();
        }}
        className="cursor-text border-[1px] border-[#e0e0e0] bg-white mb-4 rounded-[0.4rem] flex items-center px-3 py-2 gap-3"
      >
        <FontAwesomeIcon icon={faSearch} className="text-gray-500 text-lg" />
        <input
          id="table-search"
          type="text"
          placeholder={t("search") as string}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="focus:outline-none w-full text-md py-1"
        />
      </div>

      {sortedFilteredData.length > 0 ? (
        <>
          <button
            className="absolute z-20 pointer-events-none bg-brand opacity-0 focus:opacity-100 rounded-[0.4rem] text-white py-1 px-3"
            onClick={() => {
              if (window.location.hash === "#skipTable") {
                window.location.hash = "";
              }
              window.location.hash = "skipTable";
            }}
          >
            {t("skipTable")}
          </button>
          <DataGrid
            autoHeight
            disableColumnMenu={true}
            key={windowSize}
            rows={sortedFilteredData.slice(currentPage * pageSize, (currentPage + 1) * pageSize)}
            columns={processedColumns}
            onCellClick={handleCellClick}
            rowHeight={62}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            localeText={{
              columnHeaderSortIconLabel: t("sort") as string,
            }}
            components={{
              ColumnSortedAscendingIcon: UpIcon,
              ColumnSortedDescendingIcon: DownIcon,
              Footer: () => (
                <DataGridFooter>
                  <DataGridFooterPagination
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    data={sortedFilteredData.map((row) => row.id)}
                    rows={sortedFilteredData.map((row) => String(row.id))}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    filtered={search.trim() !== ""}
                  />
                </DataGridFooter>
              ),
            }}
            sx={{
              ".MuiDataGrid-columnHeaders": {
                borderRadius: "0.4rem 0.4rem 0 0",
              },
              "& .MuiDataGrid-row": {
                cursor: onRowClick ? "pointer" : "default",
              },
              '& .MuiDataGrid-cell[data-field="actions"]': {
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                padding: "0 8px 0 10px",
              },
              [`& .MuiDataGrid-columnHeader[data-field="actions"]${disableHeaderCells?.length
                ? disableHeaderCells
                  .map((field) => `, & .MuiDataGrid-columnHeader[data-field="${field}"]`)
                  .join("")
                : ""
                }`]: {
                pointerEvents: "none",
              },
            }}
          />
        </>
      ) : (
        <DataGridNoRowsOverlay filtered={search.trim() !== ""} className={rest.className} />
      )}
    </div>
  );
}

export default GenericDatagrid;
